import React from 'react';
import { Navigate, NavLink, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Checkbox,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { get, post } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDonate } from '../../contexts/PublicDonateContext';
import { formatCurrency } from '../portal/portalDonateShared';
import { cardFeeRadioSuffix, computeClientCheckoutAmounts, mergedFeeDisplay } from '../../utils/donationFeeConfig';
import { cartLineOutOfTownSurcharge, mergedOutOfTownUi, normalizeOutOfTownDays } from '../../utils/outOfTownSurcharge';
import DonateCheckoutSignInDialog from '../../components/donate/DonateCheckoutSignInDialog';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = Boolean(RECAPTCHA_SITE_KEY && String(RECAPTCHA_SITE_KEY).trim());

// --- Resilient donate finalize (order enqueue) --------------------------------
// The order is created by a single client POST to /…/finalize AFTER the payment
// is captured. To avoid "charged but no order" when that call is flaky, we retry
// with backoff and persist the pending finalize so a reload/return re-attempts it.
// The server enqueue is idempotent on payment_intent_id, so retries never dup.
const PENDING_FINALIZE_KEY = 'icct_donate_pending_finalize';

function savePendingFinalize(endpoint, payload) {
  try {
    localStorage.setItem(PENDING_FINALIZE_KEY, JSON.stringify({ endpoint, payload, ts: Date.now() }));
  } catch { /* storage blocked (private mode) — retry-in-tab still applies */ }
}
function clearPendingFinalize() {
  try { localStorage.removeItem(PENDING_FINALIZE_KEY); } catch { /* ignore */ }
}
function readPendingFinalize() {
  try {
    const raw = localStorage.getItem(PENDING_FINALIZE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p || !p.endpoint || !p.payload || !p.payload.paymentIntentId) return null;
    // Expire after 24h so a stale intent is never re-posted.
    if (Date.now() - Number(p.ts || 0) > 24 * 60 * 60 * 1000) { clearPendingFinalize(); return null; }
    return p;
  } catch { return null; }
}
async function finalizeWithRetry(endpoint, payload, attempts = 4) {
  let lastErr;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await post(endpoint, payload, { timeout: 120000 });
    } catch (e) {
      lastErr = e;
      const status = e?.status ?? e?.response?.status;
      // Retry network / 5xx; a definitive 4xx (bad request) won't succeed on retry.
      if (typeof status === 'number' && status >= 400 && status < 500) throw e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, 1000 * 2 ** i)); // 1s, 2s, 4s
    }
  }
  throw lastErr;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function buildBillingPayload(b) {
  return {
    first_name: String(b.first_name || '').trim(),
    last_name: String(b.last_name || '').trim(),
    email: String(b.email || '').trim().toLowerCase(),
    phone: String(b.phone || '').trim(),
    address_1: String(b.address_1 || '').trim(),
    address_2: String(b.address_2 || '').trim(),
    city: String(b.city || '').trim(),
    state: String(b.state || '').trim(),
    postcode: String(b.postcode || '').trim(),
    country: String(b.country || 'US').trim() || 'US',
  };
}

function formatMoney(n) {
  const x = Number(n || 0);
  return `$${x.toFixed(2)}`;
}

/** Required billing fields for the receipt (key → label shown in errors). */
const REQUIRED_BILLING_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['email', 'Email'],
  ['address_1', 'Street address'],
  ['city', 'City'],
  ['state', 'State'],
  ['postcode', 'ZIP'],
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Return a map of field key → error message for any missing/invalid required billing fields. */
function validateBilling(b) {
  const errs = {};
  REQUIRED_BILLING_FIELDS.forEach(([key, label]) => {
    if (!String(b?.[key] || '').trim()) errs[key] = `${label} is required`;
  });
  if (!errs.email && !EMAIL_RE.test(String(b?.email || '').trim())) {
    errs.email = 'Enter a valid email address';
  }
  return errs;
}

/** Line items + subtotal (same fields as cart / kiosk-style checkout summary). */
function donateCheckoutLineTotal(item) {
  const qty = Math.max(1, parseInt(item?.quantity, 10) || 1);
  return Number(item?.unit_price || 0) * qty;
}

function coerceMoney(x) {
  const n = typeof x === 'number' && Number.isFinite(x) ? x : Number(x);
  return Number.isFinite(n) ? round2(n) : NaN;
}

function DonateCheckoutOrderLines({ cart, totalAmount, summaryAmounts, feeName, outOfTownFeeName, dayRate = 251 }) {
  if (!Array.isArray(cart) || cart.length === 0) return null;
  const fromLines = cart.reduce((s, i) => s + donateCheckoutLineTotal(i), 0);
  const fromTravel = cart.reduce((s, i) => s + cartLineOutOfTownSurcharge(i, dayRate), 0);
  const n = totalAmount != null ? Number(totalAmount) : NaN;
  const footerSubtotal = !Number.isNaN(n) ? n : round2(fromLines + fromTravel);

  let base = footerSubtotal;
  let total = footerSubtotal;
  let fee = 0;
  let outOfTown = fromTravel;
  let merchandise = fromLines;
  if (summaryAmounts != null && typeof summaryAmounts === 'object') {
    const b = coerceMoney(summaryAmounts.base);
    const t = coerceMoney(summaryAmounts.total);
    const fExplicit = coerceMoney(summaryAmounts.fee);
    const oot = coerceMoney(summaryAmounts.outOfTown);
    const merch = coerceMoney(summaryAmounts.merchandise);
    if (Number.isFinite(b)) base = b;
    if (Number.isFinite(t)) total = t;
    if (Number.isFinite(oot)) outOfTown = oot;
    if (Number.isFinite(merch)) merchandise = merch;
    if (Number.isFinite(fExplicit) && fExplicit > 0) {
      fee = fExplicit;
    } else if (Number.isFinite(base) && Number.isFinite(t) && t > base + 0.001) {
      fee = round2(t - base);
    }
  }
  const showFee = fee > 0.001;
  const showTravel = outOfTown > 0.001;
  if (!showFee) {
    total = footerSubtotal;
    base = footerSubtotal;
  }
  const feeRowLabel = (feeName && String(feeName).trim()) || 'Processing fee';
  const travelRowLabel = (outOfTownFeeName && String(outOfTownFeeName).trim()) || 'Outside of Shelby County';

  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Stack spacing={2}>
        {cart.map((item) => {
          const key = `${item.product_id}-${item.variation_id || 0}`;
          const qty = Math.max(1, parseInt(item.quantity, 10) || 1);
          const line = donateCheckoutLineTotal(item);
          return (
            <Stack key={key} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {item.product_name || `Product #${item.product_id}`}
                </Typography>
                {item.variation_name ? (
                  <Typography variant="body2" color="text.secondary">
                    {item.variation_name}
                  </Typography>
                ) : null}
                {normalizeOutOfTownDays(item.out_of_town_days) > 0 ? (
                  <Typography variant="caption" sx={{ color: '#9a5500', display: 'block' }}>
                    Outside Shelby County — {normalizeOutOfTownDays(item.out_of_town_days)} day
                    {normalizeOutOfTownDays(item.out_of_town_days) === 1 ? '' : 's'} (+$
                    {cartLineOutOfTownSurcharge(item, dayRate).toFixed(2)})
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  Qty {qty} × {formatCurrency(Number(item.unit_price || 0))}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700, flexShrink: 0 }}>
                {formatCurrency(line)}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <Divider sx={{ my: 2 }} />
      {showFee || showTravel ? (
        <Stack spacing={1.25}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Service subtotal
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatCurrency(merchandise)}
            </Typography>
          </Stack>
          {showTravel ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body1" color="text.secondary">
                {travelRowLabel}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {formatCurrency(outOfTown)}
              </Typography>
            </Stack>
          ) : null}
          {showFee ? (
            <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1" color="text.secondary">
              {showTravel ? 'Subtotal before card fee' : 'Cart subtotal'}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {formatCurrency(base)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1" color="text.secondary">
              {feeRowLabel}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700 }}>
              {formatCurrency(fee)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Total
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {formatCurrency(total)}
            </Typography>
          </Stack>
            </>
          ) : (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {formatCurrency(base)}
              </Typography>
            </Stack>
          )}
        </Stack>
      ) : (
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Cart subtotal
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {formatCurrency(footerSubtotal)}
          </Typography>
        </Stack>
      )}
    </>
  );
}

function DonateCheckoutAdditionalDetailsCard({
  serviceDate,
  serviceTime,
  customerComment,
  onServiceDateChange,
  onServiceTimeChange,
  onCommentChange,
  disabled,
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Additional details
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Stack spacing={1.5} sx={{ flex: '1 1 50%', minWidth: 0 }}>
            <TextField
              label="Date of service"
              type="date"
              value={serviceDate}
              onChange={(e) => onServiceDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={disabled}
            />
            <TextField
              label="Time"
              type="time"
              value={serviceTime}
              onChange={(e) => onServiceTimeChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
              disabled={disabled}
            />
          </Stack>
          <TextField
            label="Comments (optional)"
            multiline
            minRows={3}
            maxRows={3}
            value={customerComment}
            onChange={(e) => onCommentChange(e.target.value)}
            fullWidth
            size="small"
            disabled={disabled}
            sx={{ flex: '1 1 50%', minWidth: 0 }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}

function DonateMethodAndFeeCard({ fundingType, onFundingTypeChange, displayPreview, intentErr, disabled, feeDisplay }) {
  const pct = feeDisplay.pct ?? 2.5;
  const feeBlurb =
    feeDisplay.feeCaption && String(feeDisplay.feeCaption).trim()
      ? feeDisplay.feeCaption
      : `Card and digital wallets: ${pct}% processing fee. ACH has no fee.`;
  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
          Payment method
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {feeBlurb}
        </Typography>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
        >
          <FormControl component="fieldset" disabled={disabled} sx={{ flex: '1 1 auto' }}>
            <RadioGroup value={fundingType} onChange={(e) => onFundingTypeChange(e.target.value)}>
              <FormControlLabel
                value="card_wallet"
                control={<Radio />}
                disabled={disabled}
                label={`Credit / debit card, Apple Pay, Google Pay (${cardFeeRadioSuffix(feeDisplay)})`}
              />
              <FormControlLabel
                value="ach"
                control={<Radio />}
                disabled={disabled}
                label="ACH (US bank account) — no processing fee"
              />
            </RadioGroup>
          </FormControl>
          <Box
            sx={{
              width: { xs: '100%', md: 340 },
              px: 1.5,
              py: 1.25,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              bgcolor: 'grey.50',
            }}
          >
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'right' }}>
                Subtotal (donation) <strong>{formatMoney(displayPreview.base)}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem', textAlign: 'right' }}>
                {feeDisplay.feeName} <strong>{formatMoney(displayPreview.fee)}</strong>
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, mt: 0.25, fontSize: '1.08rem', textAlign: 'right' }}>
                Total charge: {formatMoney(displayPreview.total)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
        {intentErr ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {intentErr}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PortalSessionCheckoutInner({
  checkout,
  cart,
  cartItemsPayload,
  customerComment,
  serviceDate,
  serviceTime,
  onDone,
  onCheckoutAmountsUpdate,
  syncPath,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [processingOpen, setProcessingOpen] = React.useState(false);
  const feeSyncTimerRef = React.useRef(null);
  const paymentIntentIdRef = React.useRef('');

  React.useEffect(() => {
    paymentIntentIdRef.current = String(checkout?.paymentIntentId || '');
  }, [checkout?.paymentIntentId]);

  React.useEffect(
    () => () => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
    },
    []
  );

  const queueFeeSyncForElement = React.useCallback(
    (paymentMethodType) => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
      feeSyncTimerRef.current = setTimeout(async () => {
        const pi = paymentIntentIdRef.current;
        if (!pi) return;
        try {
          const res = await post(
            syncPath,
            {
              mode: 'sync-fee',
              syncFee: true,
              paymentIntentId: pi,
              paymentMethodType,
            },
            { timeout: 30000 }
          );
          if (res?.amounts) onCheckoutAmountsUpdate(res.amounts);
        } catch (e) {
          setError(e?.message || 'Could not update fees for the selected payment method');
        }
      }, 400);
    },
    [syncPath, onCheckoutAmountsUpdate]
  );

  const onPay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setProcessingOpen(true);
    setError('');
    try {
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmErr) {
        setError(confirmErr.message || 'Payment failed');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      // Card/wallets confirm as 'succeeded'; ACH confirms as 'processing' and is
      // queued immediately when the async order queue is enabled.
      const finalizable =
        paymentIntent &&
        (paymentIntent.status === 'succeeded' ||
          (checkout?.asyncQueue && paymentIntent.status === 'processing'));
      if (!finalizable) {
        setError('Payment was not completed.');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      const finalizePayload = {
        paymentIntentId: paymentIntent.id,
        customerComment,
        serviceDate: serviceDate || undefined,
        serviceTime: serviceTime || undefined,
        items: cartItemsPayload,
      };
      // Payment is captured now — never re-enable Pay (double-charge risk). Persist
      // the finalize so a reload/return can re-attempt, and retry with backoff.
      savePendingFinalize('/portal/donate/finalize', finalizePayload);
      try {
        const fin = await finalizeWithRetry('/portal/donate/finalize', finalizePayload);
        clearPendingFinalize();
        onDone(fin);
      } catch (finErr) {
        setProcessingOpen(false);
        setError(
          `Your payment was received (ref ${paymentIntent.id}). We had trouble recording your donation — please do NOT pay again; it will be completed automatically. If you don't see it shortly, contact ICCT.`
        );
        // Keep Pay disabled (busy stays true) and keep the pending finalize.
      }
    } catch (e) {
      setError(e?.message || 'Payment failed');
      setProcessingOpen(false);
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={processingOpen} disableEscapeKeyDown onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Payment processing</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">Your payment is in progress. Please wait and do not close this page.</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
      <Stack spacing={2} sx={{ pointerEvents: processingOpen ? 'none' : 'auto', opacity: processingOpen ? 0.6 : 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {checkout?.amounts ? (
          <Typography variant="body2">
            Charging <strong>{formatMoney(checkout.amounts.total)}</strong>
            {checkout.amounts.fee > 0 ? ` (includes ${formatMoney(checkout.amounts.fee)} fee)` : ''}.
          </Typography>
        ) : null}
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
          <PaymentElement
            options={{
              wallets: { link: 'never' },
            }}
            onChange={(event) => {
              const v = event.value;
              const typ = v && typeof v.type === 'string' ? v.type : null;
              queueFeeSyncForElement(typ);
            }}
          />
        </Box>
        <Button variant="contained" size="large" onClick={onPay} disabled={busy || !stripe || !elements || processingOpen}>
          {busy ? <CircularProgress size={22} color="inherit" /> : `Donate ${formatCurrency(checkout?.amounts?.total)}`}
        </Button>
      </Stack>
    </>
  );
}

function PublicGuestLinkedCheckoutInner({
  checkout,
  cart,
  cartItemsPayload,
  billing,
  guest,
  linkToken,
  createAccountConsent,
  customerComment,
  serviceDate,
  serviceTime,
  onDone,
  onCheckoutAmountsUpdate,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [processingOpen, setProcessingOpen] = React.useState(false);
  const feeSyncTimerRef = React.useRef(null);
  const paymentIntentIdRef = React.useRef('');

  React.useEffect(() => {
    paymentIntentIdRef.current = String(checkout?.paymentIntentId || '');
  }, [checkout?.paymentIntentId]);

  React.useEffect(
    () => () => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
    },
    []
  );

  const queueFeeSyncForElement = React.useCallback(
    (paymentMethodType) => {
      if (feeSyncTimerRef.current) clearTimeout(feeSyncTimerRef.current);
      feeSyncTimerRef.current = setTimeout(async () => {
        const pi = paymentIntentIdRef.current;
        if (!pi) return;
        try {
          const body = {
            mode: 'sync-fee',
            syncFee: true,
            paymentIntentId: pi,
            paymentMethodType,
            guest,
            billing: buildBillingPayload(billing),
          };
          if (!guest && linkToken) {
            body.linkToken = linkToken;
          }
          const res = await post('/public/donate/checkout-intent', body, { timeout: 30000 });
          if (res?.amounts) onCheckoutAmountsUpdate(res.amounts);
        } catch (e) {
          setError(e?.message || 'Could not update fees for the selected payment method');
        }
      }, 400);
    },
    [guest, billing, linkToken, onCheckoutAmountsUpdate]
  );

  const onPay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setProcessingOpen(true);
    setError('');
    try {
      const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });
      if (confirmErr) {
        setError(confirmErr.message || 'Payment failed');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      // Card/wallets confirm as 'succeeded'; ACH confirms as 'processing' and is
      // queued immediately when the async order queue is enabled.
      const finalizable =
        paymentIntent &&
        (paymentIntent.status === 'succeeded' ||
          (checkout?.asyncQueue && paymentIntent.status === 'processing'));
      if (!finalizable) {
        setError('Payment was not completed.');
        setProcessingOpen(false);
        setBusy(false);
        return;
      }
      const finalizePayload = {
        paymentIntentId: paymentIntent.id,
        customerComment,
        serviceDate: serviceDate || undefined,
        serviceTime: serviceTime || undefined,
        items: cartItemsPayload,
        billing: buildBillingPayload(billing),
        createAccountConsent: guest && !!createAccountConsent,
      };
      // Payment is captured now — never re-enable Pay (double-charge risk). Persist
      // the finalize so a reload/return can re-attempt, and retry with backoff.
      savePendingFinalize('/public/donate/finalize', finalizePayload);
      try {
        const fin = await finalizeWithRetry('/public/donate/finalize', finalizePayload);
        clearPendingFinalize();
        onDone(fin);
      } catch (finErr) {
        setProcessingOpen(false);
        setError(
          `Your payment was received (ref ${paymentIntent.id}). We had trouble recording your donation — please do NOT pay again; it will be completed automatically. If you don't see it shortly, contact ICCT.`
        );
        // Keep Pay disabled (busy stays true) and keep the pending finalize.
      }
    } catch (e) {
      setError(e?.message || 'Payment failed');
      setProcessingOpen(false);
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={processingOpen} disableEscapeKeyDown onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>Payment processing</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={28} />
            <Typography variant="body1">Your payment is in progress. Please wait and do not close this page.</Typography>
          </Stack>
        </DialogContent>
      </Dialog>
      <Stack spacing={2} sx={{ pointerEvents: processingOpen ? 'none' : 'auto', opacity: processingOpen ? 0.6 : 1 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {checkout?.amounts ? (
          <Typography variant="body2">
            Charging <strong>{formatMoney(checkout.amounts.total)}</strong>
            {checkout.amounts.fee > 0 ? ` (includes ${formatMoney(checkout.amounts.fee)} fee)` : ''}.
          </Typography>
        ) : null}
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
          <PaymentElement
            options={{ wallets: { link: 'never' } }}
            onChange={(event) => {
              const v = event.value;
              const typ = v && typeof v.type === 'string' ? v.type : null;
              queueFeeSyncForElement(typ);
            }}
          />
        </Box>
        <Button variant="contained" size="large" onClick={onPay} disabled={busy || !stripe || !elements || processingOpen}>
          {busy ? <CircularProgress size={22} color="inherit" /> : `Donate ${formatCurrency(checkout?.amounts?.total)}`}
        </Button>
      </Stack>
    </>
  );
}

export default function PublicDonateCheckoutPage() {
  const navigate = useNavigate();
  const {
    cart,
    totalAmount,
    merchandiseSubtotal,
    outOfTownSubtotal,
    clearCart,
    donateMode,
    linkToken,
    verifiedEmail,
    setGuestMode,
  } = usePublicDonate();
  const { isAuthenticated, loading: authLoading, staff } = useAuth();
  const hasPortalSession = !authLoading && isAuthenticated && staff?.portal;

  const [billing, setBilling] = React.useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'US',
  });
  /** choose → billing → method → pay. Linked donors skip choose. */
  const [step, setStep] = React.useState(() => (donateMode === 'linked' ? 'billing' : 'choose'));
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [checkout, setCheckout] = React.useState(null);
  const [stripePromise, setStripePromise] = React.useState(null);
  const [fundingType, setFundingType] = React.useState('card_wallet');
  const [portalPayStep, setPortalPayStep] = React.useState('method');
  const [portalIntentErr, setPortalIntentErr] = React.useState('');
  const [serverFeeUi, setServerFeeUi] = React.useState(null);
  const [serviceDate, setServiceDate] = React.useState('');
  const [serviceTime, setServiceTime] = React.useState('');
  const [customerComment, setCustomerComment] = React.useState('');
  const [signInDialogOpen, setSignInDialogOpen] = React.useState(false);
  const [guestCheckoutRecaptchaToken, setGuestCheckoutRecaptchaToken] = React.useState(null);
  const guestCheckoutRecaptchaRef = React.useRef(null);
  /** Guest billing: opt-in devotee account provision after successful payment (default on). */
  const [createAccountConsent, setCreateAccountConsent] = React.useState(true);
  /** Per-field validation errors for the billing form (key → message). */
  const [fieldErrors, setFieldErrors] = React.useState({});

  React.useEffect(() => {
    if (hasPortalSession) setSignInDialogOpen(false);
  }, [hasPortalSession]);

  React.useEffect(() => {
    if (donateMode !== 'guest') {
      setGuestCheckoutRecaptchaToken(null);
      try {
        guestCheckoutRecaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
    }
  }, [donateMode]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await get('/public/donate/fee-ui', {});
        if (!cancelled && r?.feeUi) setServerFeeUi(r);
      } catch {
        /* build-time REACT_APP_* only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cartItemsPayload = React.useMemo(
    () =>
      cart.map((i) => {
        const row = {
          product_id: i.product_id,
          variation_id: i.variation_id || 0,
          quantity: i.quantity,
        };
        const days = normalizeOutOfTownDays(i.out_of_town_days);
        if (days > 0) row.out_of_town_days = days;
        return row;
      }),
    [cart]
  );

  const cartItemsKey = React.useMemo(() => JSON.stringify(cartItemsPayload), [cartItemsPayload]);

  React.useEffect(() => {
    if (verifiedEmail) {
      setBilling((b) => ({ ...b, email: verifiedEmail }));
    }
  }, [verifiedEmail]);

  React.useEffect(() => {
    if (!hasPortalSession) return;
    setPortalPayStep('method');
    setCheckout(null);
    setStripePromise(null);
    setPortalIntentErr('');
  }, [cartItemsKey, hasPortalSession]);

  if (authLoading) {
    return (
      <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const outOfTownDisplay = React.useMemo(
    () => mergedOutOfTownUi(serverFeeUi?.outOfTown, checkout?.outOfTown),
    [serverFeeUi, checkout?.outOfTown]
  );
  const feeDisplay = React.useMemo(
    () => mergedFeeDisplay(serverFeeUi?.feeUi, checkout?.feeUi),
    [serverFeeUi, checkout?.feeUi]
  );
  const orderLinesOutOfTownProps = {
    outOfTownFeeName: outOfTownDisplay.feeName,
    dayRate: outOfTownDisplay.dayRate,
  };
  const preview = React.useMemo(
    () => computeClientCheckoutAmounts(merchandiseSubtotal, outOfTownSubtotal, fundingType, feeDisplay),
    [merchandiseSubtotal, outOfTownSubtotal, fundingType, feeDisplay]
  );
  const displayPreviewPublic =
    step === 'pay' && checkout?.amounts
      ? { base: checkout.amounts.base, fee: checkout.amounts.fee, total: checkout.amounts.total }
      : preview;
  const displayPreviewPortal =
    portalPayStep === 'pay' && checkout?.amounts
      ? { base: checkout.amounts.base, fee: checkout.amounts.fee, total: checkout.amounts.total }
      : preview;

  const onCheckoutAmountsUpdate = React.useCallback((amounts) => {
    setCheckout((c) => (c ? { ...c, amounts } : c));
  }, []);

  // Recover a donation whose payment was captured but whose finalize (order
  // enqueue) never completed — re-attempt it on load so a reload/return still
  // records the order. Safe: the server enqueue is idempotent on payment_intent_id.
  const [recoveringFinalize, setRecoveringFinalize] = React.useState(() => !!readPendingFinalize());
  React.useEffect(() => {
    const pending = readPendingFinalize();
    if (!pending) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const fin = await finalizeWithRetry(pending.endpoint, pending.payload);
        if (cancelled) return;
        clearPendingFinalize();
        const oid = fin?.orderId || fin?.wooOrderId || '';
        navigate(`/donate/confirmation/${oid || 'processing'}`, { replace: true, state: { checkout: fin } });
        window.setTimeout(() => { try { clearCart(); } catch { /* ignore */ } }, 0);
      } catch {
        // Leave the pending entry; the payments-app reconciliation sweep is the backstop.
        if (!cancelled) setRecoveringFinalize(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, clearCart]);

  if (recoveringFinalize) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, fontWeight: 700 }}>Finalizing your donation…</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please don&apos;t close this tab or pay again.
        </Typography>
      </Box>
    );
  }

  if (!cart.length) {
    return <Navigate to="/donate/cart" replace />;
  }

  const onDone = (fin) => {
    // Async queue: the Woo order number may not exist yet — use a sentinel so the
    // route matches; the confirmation page renders from navigation state either way.
    const oid = fin?.orderId || fin?.wooOrderId || '';
    navigate(`/donate/confirmation/${oid || 'processing'}`, {
      replace: true,
      state: { checkout: fin },
    });
    // Defer clearCart: clearing first re-renders checkout with empty cart and triggers
    // `<Navigate to="/donate/cart" />` before the router applies this navigation.
    window.setTimeout(() => {
      clearCart();
    }, 0);
  };

  const setField = (k) => (e) => {
    const { value } = e.target;
    setBilling((b) => ({ ...b, [k]: value }));
    setFieldErrors((prev) => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const startPortalPaymentIntent = async () => {
    setLoading(true);
    setPortalIntentErr('');
    setError('');
    try {
      const out = await post(
        '/portal/donate/checkout-intent',
        {
          items: cartItemsPayload,
          fundingType,
        },
        { timeout: 60000 }
      );
      setCheckout(out);
      setStripePromise(loadStripe(out.publishableKey));
      setPortalPayStep('pay');
    } catch (e) {
      setPortalIntentErr(e?.message || 'Failed to initialize checkout');
    } finally {
      setLoading(false);
    }
  };

  if (hasPortalSession) {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Donate checkout
        </Typography>
        <Alert severity="info">
          You are signed in to the portal. Payment and order use your account on file.
        </Alert>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Order summary
              </Typography>
              <Button variant="outlined" size="small" component={NavLink} to="/donate/cart">
                Back to cart
              </Button>
            </Stack>
            <DonateCheckoutOrderLines
              cart={cart}
              totalAmount={totalAmount}
              summaryAmounts={portalPayStep === 'method' || portalPayStep === 'pay' ? displayPreviewPortal : undefined}
              feeName={feeDisplay.feeName}
              {...orderLinesOutOfTownProps}
            />
          </CardContent>
        </Card>

        <DonateCheckoutAdditionalDetailsCard
          serviceDate={serviceDate}
          serviceTime={serviceTime}
          customerComment={customerComment}
          onServiceDateChange={setServiceDate}
          onServiceTimeChange={setServiceTime}
          onCommentChange={setCustomerComment}
          disabled={loading}
        />

        {portalPayStep === 'method' ? (
          <>
            <DonateMethodAndFeeCard
              fundingType={fundingType}
              onFundingTypeChange={setFundingType}
              displayPreview={displayPreviewPortal}
              intentErr={portalIntentErr}
              disabled={loading}
              feeDisplay={feeDisplay}
            />
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={startPortalPaymentIntent} disabled={loading}>
                {loading ? 'Preparing payment…' : 'Continue to payment'}
              </Button>
            </Stack>
          </>
        ) : null}

        {portalPayStep === 'pay' && checkout?.clientSecret && stripePromise ? (
          <Card variant="outlined">
            <CardContent>
              <Elements stripe={stripePromise} options={{ clientSecret: checkout.clientSecret }}>
                <PortalSessionCheckoutInner
                  checkout={checkout}
                  cart={cart}
                  cartItemsPayload={cartItemsPayload}
                  customerComment={customerComment}
                  serviceDate={serviceDate}
                  serviceTime={serviceTime}
                  onDone={onDone}
                  onCheckoutAmountsUpdate={onCheckoutAmountsUpdate}
                  syncPath="/portal/donate/checkout-intent"
                />
              </Elements>
            </CardContent>
          </Card>
        ) : null}
      </Stack>
    );
  }

  const onBillingContinue = () => {
    if (!cart.length) {
      setError('Your cart is empty.');
      return;
    }
    const errs = validateBilling(billing);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      setError('Please fill in all required fields highlighted below.');
      return;
    }
    setError('');
    setStep('method');
  };

  const startPublicPaymentIntent = async () => {
    setLoading(true);
    setError('');
    try {
      if (donateMode === 'guest' && RECAPTCHA_ENABLED && !guestCheckoutRecaptchaToken) {
        setError('Please complete the reCAPTCHA verification before continuing to payment.');
        setLoading(false);
        return;
      }
      const payload = {
        items: cartItemsPayload,
        guest: donateMode === 'guest',
        billing: buildBillingPayload(billing),
        fundingType,
      };
      if (donateMode === 'linked') {
        payload.linkToken = linkToken;
      }
      if (donateMode === 'guest' && guestCheckoutRecaptchaToken) {
        payload.recaptchaToken = guestCheckoutRecaptchaToken;
      }
      const out = await post('/public/donate/checkout-intent', payload, { timeout: 60000 });
      setCheckout(out);
      setStripePromise(loadStripe(out.publishableKey));
      setStep('pay');
      setGuestCheckoutRecaptchaToken(null);
      try {
        guestCheckoutRecaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
    } catch (e) {
      setError(e?.message || 'Failed to initialize checkout');
      try {
        guestCheckoutRecaptchaRef.current?.reset();
      } catch {
        /* ignore */
      }
      setGuestCheckoutRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'choose') {
    return (
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Donate checkout
        </Typography>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Order summary
              </Typography>
              <Button variant="outlined" size="small" component={NavLink} to="/donate/cart">
                Back to cart
              </Button>
            </Stack>
            <DonateCheckoutOrderLines
              cart={cart}
              totalAmount={totalAmount}
              feeName={feeDisplay.feeName}
              {...orderLinesOutOfTownProps}
            />
          </CardContent>
        </Card>
        <DonateCheckoutAdditionalDetailsCard
          serviceDate={serviceDate}
          serviceTime={serviceTime}
          customerComment={customerComment}
          onServiceDateChange={setServiceDate}
          onServiceTimeChange={setServiceTime}
          onCommentChange={setCustomerComment}
          disabled={false}
        />
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                How would you like to check out?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in with your email, or continue as a guest?
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" color="primary" size="large" onClick={() => setSignInDialogOpen(true)}>
                  Sign in
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => {
                    setGuestMode();
                    setStep('billing');
                  }}
                >
                  Continue as guest
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <DonateCheckoutSignInDialog open={signInDialogOpen} onClose={() => setSignInDialogOpen(false)} />
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Donate checkout
      </Typography>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Order summary
            </Typography>
            <Button variant="outlined" size="small" component={NavLink} to="/donate/cart">
              Back to cart
            </Button>
          </Stack>
          <DonateCheckoutOrderLines
            cart={cart}
            totalAmount={totalAmount}
            summaryAmounts={step === 'method' || step === 'pay' ? displayPreviewPublic : undefined}
            feeName={feeDisplay.feeName}
            {...orderLinesOutOfTownProps}
          />
        </CardContent>
      </Card>

      <DonateCheckoutAdditionalDetailsCard
        serviceDate={serviceDate}
        serviceTime={serviceTime}
        customerComment={customerComment}
        onServiceDateChange={setServiceDate}
        onServiceTimeChange={setServiceTime}
        onCommentChange={setCustomerComment}
        disabled={loading}
      />

      {step === 'billing' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="subtitle2" color="text.secondary">
                {donateMode === 'linked'
                  ? 'Billing email must match your verified portal account email.'
                  : 'Enter the name and address that should appear on the receipt.'}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="First name" value={billing.first_name} onChange={setField('first_name')} required fullWidth size="small" error={!!fieldErrors.first_name} helperText={fieldErrors.first_name} />
                <TextField label="Last name" value={billing.last_name} onChange={setField('last_name')} required fullWidth size="small" error={!!fieldErrors.last_name} helperText={fieldErrors.last_name} />
              </Stack>
              <TextField
                label="Email"
                type="email"
                value={billing.email}
                onChange={setField('email')}
                required
                fullWidth
                size="small"
                disabled={donateMode === 'linked' && !!verifiedEmail}
                error={!!fieldErrors.email}
                helperText={fieldErrors.email || (donateMode === 'linked' ? 'Must match the email you verified.' : undefined)}
              />
              <TextField label="Phone" value={billing.phone} onChange={setField('phone')} fullWidth size="small" />
              <TextField label="Street address" value={billing.address_1} onChange={setField('address_1')} required fullWidth size="small" error={!!fieldErrors.address_1} helperText={fieldErrors.address_1} />
              <TextField label="Apt / suite (optional)" value={billing.address_2} onChange={setField('address_2')} fullWidth size="small" />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField label="City" value={billing.city} onChange={setField('city')} required fullWidth size="small" error={!!fieldErrors.city} helperText={fieldErrors.city} />
                <TextField label="State" value={billing.state} onChange={setField('state')} required fullWidth size="small" error={!!fieldErrors.state} helperText={fieldErrors.state} />
                <TextField label="ZIP" value={billing.postcode} onChange={setField('postcode')} required fullWidth size="small" error={!!fieldErrors.postcode} helperText={fieldErrors.postcode} />
              </Stack>
              <TextField label="Country" value={billing.country} onChange={setField('country')} fullWidth size="small" />
              {donateMode === 'guest' ? (
                <Card variant="outlined" sx={{ bgcolor: 'rgba(255, 252, 242, 0.6)' }}>
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={createAccountConsent}
                          onChange={(e) => setCreateAccountConsent(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" component="span" sx={{ lineHeight: 1.5 }}>
                          Create a devotee account with this email{' '}
                          (View this and future donations and download statements in the devotee portal.)
                        </Typography>
                      }
                      sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                  </CardContent>
                </Card>
              ) : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              <Button variant="contained" onClick={onBillingContinue}>
                Continue
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {step === 'method' ? (
        <>
          <DonateMethodAndFeeCard
            fundingType={fundingType}
            onFundingTypeChange={setFundingType}
            displayPreview={displayPreviewPublic}
            intentErr={error}
            disabled={loading}
            feeDisplay={feeDisplay}
          />
          {donateMode === 'guest' && RECAPTCHA_ENABLED ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <ReCAPTCHA
                ref={guestCheckoutRecaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => {
                  setGuestCheckoutRecaptchaToken(token);
                  setError('');
                }}
                onExpired={() => setGuestCheckoutRecaptchaToken(null)}
                onError={() => {
                  setGuestCheckoutRecaptchaToken(null);
                  setError('reCAPTCHA error. Please refresh the page and try again.');
                }}
              />
            </Box>
          ) : null}
          <Divider />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" onClick={() => setStep('billing')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={startPublicPaymentIntent}
              disabled={loading || (donateMode === 'guest' && RECAPTCHA_ENABLED && !guestCheckoutRecaptchaToken)}
            >
              {loading ? 'Preparing payment…' : 'Continue to payment'}
            </Button>
          </Stack>
        </>
      ) : null}

      {step === 'pay' && checkout?.clientSecret && stripePromise ? (
        <Card variant="outlined">
          <CardContent>
            <Elements stripe={stripePromise} options={{ clientSecret: checkout.clientSecret }}>
              <PublicGuestLinkedCheckoutInner
                checkout={checkout}
                cart={cart}
                cartItemsPayload={cartItemsPayload}
                billing={billing}
                guest={donateMode === 'guest'}
                linkToken={donateMode === 'linked' ? linkToken : undefined}
                createAccountConsent={donateMode === 'guest' ? createAccountConsent : false}
                customerComment={customerComment}
                serviceDate={serviceDate}
                serviceTime={serviceTime}
                onDone={onDone}
                onCheckoutAmountsUpdate={onCheckoutAmountsUpdate}
              />
            </Elements>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
}
